/**
 * Unified API helper for Edge browser compatibility
 * Standardizes all fetch requests with consistent headers, error handling, and auth
 */

const API_BASE = process.env.REACT_APP_BACKEND_URL;

/**
 * Makes an API call with consistent configuration for browser compatibility
 * @param {string} endpoint - API endpoint (e.g., '/api/auth/login')
 * @param {Object} options - Request options
 * @param {string} options.method - HTTP method (GET, POST, PUT, DELETE)
 * @param {Object} options.body - Request body (will be JSON stringified)
 * @param {Object} options.headers - Additional headers
 * @param {boolean} options.includeAuth - Whether to include auth token (default: true)
 * @returns {Promise<any>} - Parsed JSON response
 */
export const apiCall = async (endpoint, options = {}) => {
  const { 
    method = 'GET', 
    body, 
    headers = {}, 
    includeAuth = true 
  } = options;

  const token = includeAuth ? localStorage.getItem('auth_token') : null;
  const url = `${API_BASE}${endpoint}`;
  
  const config = {
    method,
    headers: {
      'Accept': 'application/json',
      'Content-Type': 'application/json',
      'Cache-Control': 'no-cache',
      'Pragma': 'no-cache',
      ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
      ...headers
    },
    credentials: 'same-origin',
    cache: 'no-cache',
    mode: 'cors'
  };
  
  if (body) {
    config.body = typeof body === 'string' ? body : JSON.stringify(body);
  }
  
  const response = await fetch(url, config);
  
  if (!response.ok) {
    let errorMessage = `Request failed: ${response.status}`;
    try {
      const error = await response.json();
      errorMessage = error.detail || error.message || errorMessage;
    } catch {
      // Ignore JSON parse errors for error response
    }
    throw new Error(errorMessage);
  }
  
  // Handle empty responses (e.g., 204 No Content)
  const contentType = response.headers.get('content-type');
  if (!contentType || !contentType.includes('application/json')) {
    return null;
  }
  
  return response.json();
};

/**
 * Makes an API call without authentication
 * Useful for public endpoints like login, register, etc.
 */
export const publicApiCall = async (endpoint, options = {}) => {
  return apiCall(endpoint, { ...options, includeAuth: false });
};

export default apiCall;
